import { z } from 'zod'
import { useQuery } from '@tanstack/vue-query'
import type { PaginationState } from '@tanstack/vue-table'

import {
  CustomFieldType,
  type ContentTypeModelName,
  type CustomFieldListQueryParams,
  type CustomFieldAttributes,
  type PairedCustomField,
} from '@/models/customFields.js'
import { useContentTypeQuery } from '@/composables/contentType.js'
import { useApi } from '@/composables/useApi.js'
import { useFeatureBlocks } from '@/composables/useFeatureBlocks.js'
import useTranslation from '@/composables/useTranslation.js'

function groupCustomFields(
  fields: CustomFieldAttributes[],
): PairedCustomField[] {
  const [checkboxes, fieldsWithHints, fieldsWithoutHints] = fields.reduce(
    (acc, field) => {
      if (field.type === CustomFieldType.Checkbox) {
        acc[0].push(field)
      } else if (field.hint) {
        acc[1].push(field)
      } else {
        acc[2].push(field)
      }

      return acc
    },
    [[], [], []] as CustomFieldAttributes[][],
  )

  return [
    ...(fieldsWithoutHints.length
      ? [{ fields: fieldsWithoutHints, spanFullWidth: false }]
      : []),
    ...(fieldsWithHints.length
      ? [{ fields: fieldsWithHints, spanFullWidth: false }]
      : []),
    ...checkboxes.map((field) => ({
      fields: [field],
      spanFullWidth: true,
    })),
  ]
}

const customFieldKeys = {
  all: ['customFields'] as const,
  lists: () => [...customFieldKeys.all, 'list'] as const,
  list: (params: MaybeRef<CustomFieldListQueryParams>) =>
    [...customFieldKeys.lists(), params] as const,
  details: () => [...customFieldKeys.all, 'detail'] as const,
  detail: (id: string) => [...customFieldKeys.details(), id] as const,
}

export function useCustomFieldsListQuery({
  enabled = true,
  pagination = { pageIndex: 0, pageSize: 50 },
  filters = {},
  includes = ['options.option_details'],
}: {
  enabled?: MaybeRef<boolean>
  pagination?: MaybeRef<PaginationState>
  filters?: MaybeRef<
    Omit<CustomFieldListQueryParams, 'page[number]' | 'page[size]' | 'include'>
  >
  includes?: MaybeRef<string[]>
} = {}) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed(() => {
    const paginationValue = unref(pagination)
    const filtersValue = unref(filters)
    const includesValue = unref(includes)

    return {
      ...filtersValue,
      'page[number]': paginationValue.pageIndex + 1,
      'page[size]': paginationValue.pageSize,
      include: includesValue,
    }
  })

  const query = useQuery({
    enabled,
    queryKey: customFieldKeys.list(queryParams),
    queryFn: async () => {
      const response = await api((client) =>
        client.GET('/{provet_id}/api/custom_fields', {
          params: {
            path: { provet_id: route.params.provetId },
            query: queryParams.value,
          },
        }),
      )

      return response
    },
  })

  return reactive(query)
}

export function useCustomFieldsByContentTypeQuery({
  contentTypeIds,
  enabled = true,
}: {
  contentTypeIds: MaybeRef<number[]>
  enabled?: MaybeRef<boolean>
}) {
  const filters = computed<CustomFieldListQueryParams>(() => ({
    'filter[content_type_id.in]': unref(contentTypeIds),
    'filter[archived]': false,
    // Only include non-JSON fields
    'filter[field_type.in]': [
      CustomFieldType.CharField,
      CustomFieldType.IntegerField,
      CustomFieldType.DateField,
      CustomFieldType.Checkbox,
      CustomFieldType.SingleSelection,
      CustomFieldType.MultiSelection,
    ],
  }))

  return useCustomFieldsListQuery({
    enabled,
    filters,
    includes: ['options.option_details'],
  })
}

export function useCustomFieldsForModel(
  modelName: ContentTypeModelName,
  attributeName = 'custom_fields',
) {
  const { visibleFeatureBlocks } = useFeatureBlocks()
  const tComponent = useTranslation('common.generalValidations')

  const isEnabled = computed(() =>
    visibleFeatureBlocks.value.includes('custom_fields'),
  )

  const contentTypeQuery = useContentTypeQuery({
    modelName,
    enabled: isEnabled,
  })
  const customFieldsQuery = useCustomFieldsByContentTypeQuery({
    contentTypeIds: computed(() =>
      contentTypeQuery.data ? [Number(contentTypeQuery.data)] : [],
    ),
    enabled: computed(() => !!contentTypeQuery.data && !!isEnabled.value),
  })

  const getOptionAttributes = (id: string, type: string) => {
    const item = customFieldsQuery.data?.included?.find(
      (option) => option.type === type && option.id === id,
    )

    const optionData = (
      item?.relationships?.option_details as {
        data?: { type: string; id: string }
      }
    )?.data

    const attributes = customFieldsQuery.data?.included?.find(
      (option) =>
        option.type === optionData?.type && option.id === optionData?.id,
    )?.attributes

    return {
      text: attributes?.text,
      archived: attributes?.archived,
      isDefaultValue: item?.attributes?.default,
    }
  }

  const getSelectedDefaultValue = (
    type: string,
    data: {
      id: string
      isDefaultValue: boolean
    }[],
  ) =>
    type === CustomFieldType.MultiSelection
      ? data
          ?.filter((option) => option.isDefaultValue)
          .map((option) => option.id)
      : data?.find((option) => option.isDefaultValue)?.id

  const customFields = computed<CustomFieldAttributes[]>(() =>
    (customFieldsQuery.data?.data || [])
      .filter((field) => field.attributes?.name && field.attributes?.field_type)
      .map((field) => {
        const isSelectionField =
          field.attributes!.field_type === CustomFieldType.SingleSelection ||
          field.attributes!.field_type === CustomFieldType.MultiSelection

        const options = isSelectionField
          ? field.relationships?.options?.data?.map((option) => {
              const optionAttributes = getOptionAttributes(
                option.id,
                option.type,
              )

              return {
                id: option.id,
                text: (optionAttributes?.text as string) ?? '',
                archived: (optionAttributes?.archived as boolean) ?? false,
                isDefaultValue: !!optionAttributes?.isDefaultValue,
              }
            })
          : undefined

        return {
          id: field.id,
          name: attributeName ? `${attributeName}.${field.id}` : field.id,
          options,
          defaultValue: isSelectionField
            ? getSelectedDefaultValue(
                field.attributes!.field_type,
                options ?? [],
              )
            : // @ts-expect-error - schema is flacky here
              field.attributes?.default?.val,
          label: field.attributes!.name,
          type: field.attributes!.field_type,
          required: !!field.attributes?.is_required,
          hint: field.attributes?.help_text || '',
        }
      }),
  )
  const validationSchema = computed(() => {
    const fields = customFields.value.reduce(
      (acc, field) => {
        const isRequired = field.required

        let fieldSchema: z.ZodTypeAny

        switch (field.type) {
          case CustomFieldType.CharField:
          case CustomFieldType.DateField:
          case CustomFieldType.SingleSelection:
            fieldSchema = z.preprocess(
              (val) => val || undefined,
              isRequired
                ? z.string().min(1, tComponent('required'))
                : z.string().optional(),
            )
            break
          case CustomFieldType.IntegerField: {
            const message = tComponent('number')

            fieldSchema = isRequired
              ? z.number({ message })
              : z.number({ message }).optional()
            break
          }
          case CustomFieldType.Checkbox:
            fieldSchema = isRequired ? z.boolean() : z.boolean().optional()
            break
          case CustomFieldType.MultiSelection:
            fieldSchema = isRequired
              ? z.array(z.string()).min(1, tComponent('atLeastOneOption'))
              : z.array(z.string()).optional()
            break
          default:
            fieldSchema = z.unknown()
        }

        // remove prefix to avoid double nesting when schema is wrapped below
        acc[field.name.replace(`${attributeName}.`, '')] = fieldSchema

        return acc
      },
      {} as Record<string, z.ZodTypeAny>,
    )

    return attributeName
      ? z.object({
          [attributeName]: z.object(fields),
        })
      : z.object(fields)
  })

  const pairedCustomFields = computed(() => ({
    all: groupCustomFields(customFields.value),
    required: groupCustomFields(customFields.value.filter((f) => f.required)),
    optional: groupCustomFields(customFields.value.filter((f) => !f.required)),
  }))

  return {
    customFields,
    pairedCustomFields,
    validationSchema,
  }
}
